/**
 * threads/{id} + threads/{id}/messages/{id} — Phase 2A messaging core.
 *
 * Schema:
 *   threads/{threadId}:
 *     participantUids[]     uids of everyone in the conversation
 *     kind                  'dm' (2 participants) | 'group' (3-25 — group
 *                           support lands next PR; rules cap is already 25)
 *     title                 free-text for group threads; null for DMs
 *                           (we render the other person's name instead)
 *     lastMessageText       denormalized preview, drives inbox row
 *     lastMessageAt         denormalized, drives inbox sort
 *     lastMessageBy         uid of last sender (for "you: ..." prefix)
 *     createdAt / createdBy
 *
 *   threads/{threadId}/messages/{messageId}:
 *     authorUid             must equal the auth uid at write time (rule)
 *     text                  the message body
 *     createdAt             server-stamped
 *
 * DM dedup: findOrCreateDm() searches my threads for an existing dm with
 * a given other uid before creating a new one. Avoids the "I messaged
 * Kim three times and now I have three inboxes" foot-gun.
 *
 * Real-time: both useMyThreads() and useThread() subscribe via
 * onSnapshot so the inbox + open thread update live as the other side
 * types.
 */
import { hasRichMessageFormatting, plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from 'firebase/auth';
import {
  collection,
  collectionGroup,
  deleteField,
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type FieldValue,
  Timestamp,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
} from 'firebase/storage';
import { uploadBytes, uploadBytesResumable, withUploadTimeout } from './storageUpload';
import { downscaleImage } from './offerings';
import { db, storage } from './firebase';
import { useAuth } from './auth-context';
import { useUserDoc, useUsersList, type UserDoc } from './users';
import {
  canonicalJrStaffAliasUid,
  isOwnJrStaffSignIn,
  jrStaffAliasUidsFor,
  JR_STAFF_ALIAS_MAX,
  JR_STAFF_ALIAS_STAFF_ROLES,
  type JrStaffAliasAccount,
} from './jrStaffAliases.ts';
import { functionHeaders } from './functionSecurity';
import {
  LISTENER_RETRY_MAX_ATTEMPTS,
  firestoreListenerErrorCode,
  isTransientFirestoreListenerErrorCode,
  listenerRetryDelayMs,
} from './boardMembershipOverlay';
import {
  communityMessageNotifyOptions,
  messageNotificationSenderName,
} from './communityLabels';
import { notifyUsers } from './notifications';
import {
  FAMILY_ATTENTION_CHANGED_EVENT,
  acknowledgeFamilyAttention,
  createFamilyMessage as createServerFamilyMessage,
  listFamilyAttention,
  type FamilyAttentionPage,
} from './familyMessages';
import {
  MAX_CONCURRENT_SENT_READS,
  sentThreadReadPlan,
  type SentThreadVersion,
} from './sentMessageView';
import { shouldNotifyThreadMessage } from './threadNotifyDecision';
import { messageStillNeedsMyAck } from './staffWaitingOnYouCore';
import { useReviveAfterError } from './listenerRevival';
import { MAX_GROUP_OTHERS } from './threadLimits';
import { stampedParticipantLabel } from './threadLabels';
import { createDirectoryCache, type DirectoryCacheLoad } from './directoryCache';
import { messagePreviewText, threadInboxPreviewText } from './messagePreview';
import { recallMessage } from './messageRecallWrite';
// The upload code loads on the first upload, not with the first screen
// (speed plan 2026-10-19, P2: it was on the startup path for every visitor).
const loadAttachmentGateway = () => import('./attachmentUploadGateway');
import { saveMessageWithOptionalAttachments } from './messageAttachmentWrite';

export type ThreadKind = 'dm' | 'group';

export const MAX_MESSAGE_ATTACHMENTS = 4;
const MESSAGE_IMAGE_ATTACHMENT_MAX_BYTES = 8 * 1024 * 1024;
// 6 GB, RESIZED 2026-08-18 alongside the gateway's ATTACHMENT_VIDEO_MAX_BYTES
// (attachmentUpload.mjs has the sizing math): a full dance shared inline in
// an EXISTING thread, which is the only lane this constant governs - see
// useUploadMessageImageAttachment below. That lane uploads through
// uploadAttachmentViaGateway only, never a direct Storage SDK write, so
// there is no shared/storage.rules ceiling to stay under here.
const MESSAGE_VIDEO_ATTACHMENT_MAX_BYTES = 6 * 1024 * 1024 * 1024;
// The FIRST whole-family message has no thread id yet, so its attachment
// stages through a direct Firebase Storage SDK write to
// family-message-attachments/ instead of the gateway - see
// pendingFamilyMessageAttachmentPath and useUploadPendingFamilyMessageAttachment
// below. That write is gated by shared/storage.rules' isMessageVideoUpload(),
// RAISED to 6 GB in the same change that raises this constant, so this lane
// now matches the gateway lane above. (useUploadPendingFamilyMessageAttachment
// has no caller anywhere in hub/src today - this constant only protects
// against the swallowed-upload trap if that lane is ever wired back up.)
const PENDING_FAMILY_MESSAGE_VIDEO_MAX_BYTES = 6 * 1024 * 1024 * 1024;
// Matches the gateway's 25 MB safe-file cap.
const MESSAGE_FILE_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
// The initial whole-family-message staging lane predates a thread id. Small
// files get a bounded upload; larger files use resumable progress.
const QUICK_UPLOAD_MAX_BYTES = 2.5 * 1024 * 1024;

/**
 * The deliberate plain-file allowlist, mirrored by the secure upload gateway
 * and the kind == 'file' branch of hasMessageAttachmentShape(). PDF,
 * audio (solo music), text/CSV, and the two modern Office types. text/html
 * and image/svg+xml stay out because both can carry scripts; archives and
 * executables stay out because a message thread every participant can
 * download from is exactly where those do damage.
 */
export function isMessageFileContentType(contentType: string): boolean {
  return (
    contentType === 'application/pdf' ||
    contentType.startsWith('audio/') ||
    contentType === 'text/plain' ||
    contentType === 'text/csv' ||
    contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
}
export interface ThreadDoc {
  /** Server-owned Admin board group marker. */
  adminBoard?: boolean;
  id: string;
  participantUids: string[];
  /** Safe display labels stamped by server-created community threads. */
  participantLabels?: Record<string, string>;
  /** Shared class that authorized a parent-to-parent community thread. */
  communityOfferingId?: string;
  /** Server-expanded whole-family conversation. */
  familyThread?: boolean;
  /**
   * A conversation an older dancer started with the studio team from her
   * dancer home (netlify/functions/lib/dancerStaffThreads.mjs). Server-owned.
   */
  dancerStaffThread?: boolean;
  familyStudentIds?: string[];
  familyGuardianStudentIds?: Record<string, string[]>;
  familyDancerStudentIds?: Record<string, string>;
  familyStaffProofs?: Array<{ offeringId: string; studentId: string }>;
  kind: ThreadKind;
  title: string | null;
  lastMessageText: string | null;
  lastMessageAt: Timestamp | FieldValue | null;
  lastMessageBy: string | null;
  /** Immutable message id authorizing the current denormalized preview. */
  lastMessageId?: string | null;
  /** Latest inbound message time for each participant. */
  lastInboundAtByUid?: Record<string, Timestamp | FieldValue>;
  createdAt: Timestamp | FieldValue;
  createdBy: string;
}

export interface MessageAttachment {
  id: string;
  kind: 'image' | 'video' | 'file';
  url: string;
  name: string;
  contentType: string;
  storagePath?: string;
}

export interface MessageDoc {
  id: string;
  authorUid: string;
  text: string;
  /**
   * uids of thread participants @-mentioned in this message. Empty
   * array if no one was mentioned. Populated at send time by the
   * Thread composer based on the @-trigger autocomplete picks.
   * Drives the Mentions inbox via collection-group query.
   */
  mentions: string[];
  /**
   * If true, the author marked this message as ack-required. Other
   * participants see an "Acknowledge" button until they ack. The flag
   * is immutable once sent — the only updatable field on a message is
   * the acks map (and only your own key in it).
   */
  ackRequired: boolean;
  /**
   * uid → server timestamp of when that user acked. Empty when no one
   * has acked yet. The author is never required to ack themselves.
   */
  acks: Record<string, Timestamp | FieldValue>;
  /**
   * Snapshot of the participant uids expected to acknowledge this message.
   * Set at send time for ack-required messages so counts stay stable even
   * if someone later leaves the thread.
   */
  expectedAckUids?: string[];
  /**
   * Server-only acknowledgement snapshot for a whole-family thread. Keeping
   * this out of `expectedAckUids` prevents the global unacked query from
   * bypassing the thread's live guardian-membership check after an unlink.
   */
  familyExpectedAckUids?: string[];
  /**
   * Set true when the author recalls their message. The
   * `text` is blanked and `mentions` cleared at recall time; the bubble
   * renders a "recalled" tombstone instead of content. Absent/false on
   * normal messages.
   */
  recalled?: boolean;
  recalledAt?: Timestamp | FieldValue;
  /** Set when the author edits the message text after sending. */
  editedAt?: Timestamp | FieldValue;
  /** Number of immutable before-edit records kept beneath this message. */
  revisionCount?: number;
  /** Most recent immutable revision document written with the body edit. */
  lastRevisionId?: string;
  /** Per-participant server timestamps used for read receipts in small chats. */
  readBy?: Record<string, Timestamp | FieldValue>;
  /** Image/GIF/video attachments shared inside message threads, by any active participant. */
  attachments?: MessageAttachment[];
  /** Server time of the latest background attachment append; extends Recall, not Edit. */
  attachmentAppendedAt?: Timestamp | FieldValue;
  /** One emoji reaction per participant uid. */
  reactions?: Record<string, string>;
  createdAt: Timestamp | FieldValue;
}

/*
 * userHandle and threadMentionHandles moved to lib/threadLabels.ts (Codex
 * round 6 of #796) so the whole mention-handle system sits in one importless
 * module a node test can EXECUTE: this file pulls the Firebase client graph,
 * which is why round 5's handle assertions could only read its source. They
 * are re-exported here because existing importers (StaffAnnouncementRow,
 * Thread, ThreadRow) reach for them at this path.
 */
export { threadMentionHandles, userHandle } from './threadLabels';

/**
 * How long a DM open will wait for the staff directory before giving up.
 * Long enough for a cold listener on a slow connection, short enough that a
 * person who tapped twice has not walked away.
 */
const DIRECTORY_SETTLE_TIMEOUT_MS = 4_000;
const DIRECTORY_SETTLE_POLL_MS = 50;

/**
 * Wait for a useUsersList result to stop loading, bounded. Returns whatever
 * the ref holds at the end, loading flag included, so the caller decides what
 * an unsettled directory means rather than having a fallback chosen for it.
 */
async function settledDirectory(
  ref: { current: { users: UserDoc[]; loading: boolean } }
): Promise<{ users: UserDoc[]; loading: boolean }> {
  const deadline = Date.now() + DIRECTORY_SETTLE_TIMEOUT_MS;
  while (ref.current.loading && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, DIRECTORY_SETTLE_POLL_MS));
  }
  return ref.current;
}

function dmThreadId(uidA: string, uidB: string): string {
  return `dm_${[uidA, uidB].sort().join('_')}`;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface UseMyThreadsResult {
  loading: boolean;
  threads: ThreadDoc[];
  error: string | null;
  /** True once the directory and every listed thread have reached the server. */
  confirmed: boolean;
  /**
   * Tear the whole subscription set down and start again with a fresh retry
   * budget. Wired to the Try again control beside the error banner: once the
   * bounded automatic retries are spent this hook STOPS, so a person needs a
   * way to ask again that is not "reload the page".
   */
  retry: () => void;
}

/**
 * What a person is told when the bounded retries are spent. Same sentence
 * the directory fetch already uses for its own failure, so the inbox has one
 * honest failure line rather than two that mean the same thing.
 */
export const THREAD_LISTENER_FAILED_MESSAGE = 'Your conversations could not load. Try again.';

/**
 * HER OWN PHONE (DECISIONS 150). A reply in a conversation a dancer started
 * with the studio team buzzes her plain dancer sign-in, which is not a
 * Firebase account, so no notifyUsers call can reach it, and a promoted
 * Jr Staff dancer login, which a reply in a group conversation never tells.
 * The server stages it
 * after re-reading everything (netlify/functions/dancerStaffThreadAlert.mjs):
 * this call only names the message. Best-effort, like notifyUsers: the
 * message is already sent, so a failure is logged and never thrown.
 */
async function requestDancerStaffThreadAlert(user: User, threadId: string, messageId: string): Promise<void> {
  try {
    const response = await fetch('/.netlify/functions/dancerStaffThreadAlert', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: JSON.stringify({ threadId, messageId }),
    });
    if (!response.ok) console.error('sendThreadMessage: dancer alert failed', response.status);
  } catch (e) {
    console.error('sendThreadMessage: dancer alert failed', e);
  }
}

async function fetchThreadIdsFromServer(
  user: User,
  options: { ordinaryOnly?: boolean; otherUid?: string } = {}
): Promise<string[]> {
  const params = new URLSearchParams();
  if (options.ordinaryOnly) params.set('scope', 'ordinary');
  if (options.otherUid) params.set('otherUid', options.otherUid);
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  const response = await fetch(`/.netlify/functions/threadDirectory${suffix}`, {
    method: 'GET',
    headers: await functionHeaders(user, { json: false }),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    threadIds?: unknown;
    error?: string;
  };
  if (!response.ok) {
    throw new Error(payload.error === 'too many requests'
      ? 'Messages refreshed a lot. Wait a moment, then try again.'
      : 'Your conversations could not load. Try again.');
  }
  return Array.isArray(payload.threadIds)
    ? payload.threadIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
}

/**
 * The thread-id list is server-authorized and rate-limited per account
 * (threadDirectory.mjs). It used to be refetched on every inbox mount, every
 * conversation opened, every window focus and every 30 second poll, which is
 * how a person reading her own inbox spent the whole budget and was told
 * "Messages refreshed a lot. Wait a moment, then try again." (Bobbie,
 * 2026-08-13). A short session cache makes plain navigation free, while the
 * freshness window sits just under the 30 second inbox poll so each poll tick
 * still does real work and a brand-new conversation appears within half a
 * minute, same as before.
 *
 * Only the no-options list is cached. The filtered variants (ordinaryOnly,
 * otherUid) stay direct: they are rare, user-initiated, and parameterized.
 */
const THREAD_IDS_FRESH_MS = 25_000;
const threadIdCache = createDirectoryCache<string[]>(THREAD_IDS_FRESH_MS);

function loadMyThreadIds(
  user: User,
  options: { forceFresh?: boolean } = {}
): Promise<DirectoryCacheLoad<string[]>> {
  return threadIdCache.load(user.uid, () => fetchThreadIdsFromServer(user), options);
}

/**
 * The page's document when it can report visibility, else null. Anything
 * without one (a test runtime, a worker) behaves exactly as before: never
 * hidden, and no visibility listener.
 */
function threadVisibilityTarget(): Pick<Document, 'addEventListener' | 'removeEventListener'> | null {
  if (typeof document === 'undefined' || !document) return null;
  return typeof document.addEventListener === 'function' && typeof document.removeEventListener === 'function'
    ? document
    : null;
}

/** True only when a real page says it is not on screen (A-01, useMyThreads' poll). */
function threadTabHidden(): boolean {
  if (typeof document === 'undefined' || !document) return false;
  const state = document.visibilityState;
  return typeof state === 'string' && state !== 'visible';
}

/**
 * Subscribe to every thread the signed-in user is a participant in,
 * newest activity first. Inbox uses this for the main list.
 */
export function useMyThreads(trackConfirmation = false): UseMyThreadsResult {
  const { user } = useAuth();
  const [threads, setThreads] = useState<ThreadDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!db || !user) {
      setThreads([]);
      setLoading(false);
      setConfirmed(false);
      return;
    }
    const firestore = db;
    let disposed = false;
    let refreshing = false;
    let directoryConfirmed = false;
    const docsById = new Map<string, ThreadDoc>();
    const subscriptions = new Map<string, () => void>();
    const serverSettledIds = new Set<string>();
    /**
     * THE QUOTA AMPLIFIER, and why the budget is per MOUNT rather than per
     * thread (Firestore daily read quota hit on the class night of
     * 2026-09-08). This error callback used to take no error argument, do no
     * code check, and unconditionally `refresh(true)` - which bypasses the 25
     * second directory cache. A person with eighty conversations has eighty of
     * these listeners, so ONE quota refusal made all eighty refetch the
     * directory and resubscribe all eighty threads, fail, and do it again:
     * roughly 160 reads a cycle, forever, against the quota that had just
     * refused them. A per-thread budget of three would still be 240
     * resubscribes. One shared budget, with every concurrent failure coalesced
     * into a single scheduled round, caps the whole mount at three.
     */
    let transientRetryCount = 0;
    let retryTimer: number | null = null;
    let retriesSpent = false;
    const pendingRetryIds = new Set<string>();
    const updateConfirmation = () => {
      if (disposed) return;
      setConfirmed(
        directoryConfirmed &&
          Array.from(subscriptions.keys()).every((id) => serverSettledIds.has(id))
      );
    };
    const emit = () => {
      const docs = Array.from(docsById.values());
      docs.sort((a, b) => {
        const aT = (a.lastMessageAt as Timestamp | null)?.toMillis?.() ?? 0;
        const bT = (b.lastMessageAt as Timestamp | null)?.toMillis?.() ?? 0;
        return bT - aT;
      });
      setThreads(docs);
    };
    const subscribeThread = (id: string) => {
      if (disposed || subscriptions.has(id)) return;
      const unsubscribe = onSnapshot(
        doc(firestore, 'threads', id),
        { includeMetadataChanges: trackConfirmation },
        (snap) => {
          if (snap.exists()) {
            docsById.set(id, { id, ...(snap.data() as Omit<ThreadDoc, 'id'>) });
          } else {
            docsById.delete(id);
          }
          if (!snap.metadata.fromCache) {
            serverSettledIds.add(id);
            // ONLY A SERVER-CONFIRMED SNAPSHOT REFILLS THE BUDGET. A cached
            // emission is exactly what a resubscribe gets from disk while the
            // same refusal is still in force, so refilling on one would turn
            // the bounded budget back into the unbounded loop it replaced
            // (the same trap boardMembership.ts records at round 18).
            transientRetryCount = 0;
          }
          emit();
          updateConfirmation();
        },
        (caught) => {
          docsById.delete(id);
          serverSettledIds.delete(id);
          setConfirmed(false);
          const failedSubscription = subscriptions.get(id);
          if (failedSubscription) failedSubscription();
          subscriptions.delete(id);
          emit();
          const code = firestoreListenerErrorCode(caught);
          if (isTransientFirestoreListenerErrorCode(code)) {
            // resource-exhausted, unavailable, deadline-exceeded and friends
            // say nothing at all about whether this person is still in this
            // thread, so the directory is not wrong and MUST NOT be refetched.
            // Resubscribe off the list already in hand, bounded, and then stop.
            scheduleTransientResubscribe(id);
            return;
          }
          // The list this subscription came from just proved wrong for
          // this thread (left it, or authorization rotated). Ask the
          // server rather than resubscribing off the same cached list
          // forever; concurrent failures coalesce into one request.
          void refresh(true);
        }
      );
      subscriptions.set(id, unsubscribe);
    };
    const scheduleTransientResubscribe = (id: string) => {
      if (disposed || retriesSpent) return;
      pendingRetryIds.add(id);
      // One timer for the whole mount: eighty listeners failing in the same
      // instant buy one resubscribe round between them, not eighty.
      if (retryTimer !== null) return;
      if (transientRetryCount >= LISTENER_RETRY_MAX_ATTEMPTS) {
        retriesSpent = true;
        pendingRetryIds.clear();
        setLoading(false);
        setError(THREAD_LISTENER_FAILED_MESSAGE);
        return;
      }
      transientRetryCount += 1;
      const delay = listenerRetryDelayMs(transientRetryCount);
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        if (disposed || retriesSpent) return;
        const ids = Array.from(pendingRetryIds);
        pendingRetryIds.clear();
        for (const pendingId of ids) subscribeThread(pendingId);
      }, delay);
    };
    const refresh = async (forceFresh = false) => {
      // Once the budget is spent this hook is stopped on purpose. The 30
      // second poll must not quietly restart the same loop behind the honest
      // failure line; only the Try again control does that, by remounting.
      if (refreshing || disposed || retriesSpent) return;
      refreshing = true;
      try {
        // A caller using confirmation to authorize an empty-state sentence
        // needs a directory answer for this mount, not a server answer cached
        // in this tab up to 25 seconds ago. Non-opted-in callers retain the
        // existing coalesced session cache behavior.
        const { value: ids } = await loadMyThreadIds(user, {
          forceFresh: (trackConfirmation && !directoryConfirmed) || forceFresh,
        });
        if (disposed) return;
        directoryConfirmed = true;
        const nextIds = new Set(ids);
        for (const [id, unsubscribe] of subscriptions) {
          if (!nextIds.has(id)) {
            unsubscribe();
            subscriptions.delete(id);
            docsById.delete(id);
            serverSettledIds.delete(id);
          }
        }
        for (const id of ids) subscribeThread(id);
        emit();
        setError(null);
        updateConfirmation();
      } catch (caught) {
        if (!disposed) {
          directoryConfirmed = false;
          setConfirmed(false);
          setError(caught instanceof Error ? caught.message : String(caught));
        }
      } finally {
        if (!disposed) setLoading(false);
        refreshing = false;
      }
    };
    const onFocus = () => void refresh();
    // A HIDDEN TAB SKIPS THE POLL (readiness audit 2026-10-03, A-01). Every
    // tick is a threadDirectory call that reads every conversation this
    // person has ever had, and a tab left open in the background used to
    // make one every 30 seconds for nobody. The tick now does nothing while
    // the tab is hidden, and the tab coming back refreshes once (the 25
    // second session cache keeps that free when it was only away a moment).
    const onTick = () => {
      if (!threadTabHidden()) void refresh();
    };
    const onVisibility = () => {
      if (!threadTabHidden()) void refresh();
    };
    void refresh();
    const interval = window.setInterval(onTick, 30_000);
    window.addEventListener('focus', onFocus);
    const visibilityTarget = threadVisibilityTarget();
    visibilityTarget?.addEventListener('visibilitychange', onVisibility);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      window.removeEventListener('focus', onFocus);
      visibilityTarget?.removeEventListener('visibilitychange', onVisibility);
      for (const unsubscribe of subscriptions.values()) unsubscribe();
    };
  }, [trackConfirmation, user, retryToken]);

  return { loading, threads, error, confirmed, retry };
}

export interface UseThreadResult {
  loading: boolean;
  thread: ThreadDoc | null;
  messages: MessageDoc[];
  error: string | null;
}

/**
 * Subscribe to a single thread + its messages, oldest first (chat order).
 */
export function useThread(threadId: string | undefined): UseThreadResult {
  const { user } = useAuth();
  const [thread, setThread] = useState<ThreadDoc | null>(null);
  const [messages, setMessages] = useState<MessageDoc[]>([]);
  const [loadingThread, setLoadingThread] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !threadId || !user) {
      setThread(null);
      setMessages([]);
      setLoadingThread(false);
      setLoadingMessages(false);
      return;
    }

    const firestore = db;
    let disposed = false;
    let unsubThread: (() => void) | null = null;
    let unsubMessages: (() => void) | null = null;
    setThread(null);
    setMessages([]);
    setLoadingThread(true);
    setLoadingMessages(true);
    setError(null);

    // One recovery per mount. A rules refusal on a listener the id list
    // vouched for usually means the cached authorization aged out mid-view,
    // most often a whole-family thread whose index-zero proof went stale and
    // needs the directory transaction to promote a live one. One forced
    // directory call re-promotes it and one resubscribe restores the screen.
    // A second refusal after that is real and surfaces as an error.
    let recovery: 'unused' | 'active' | 'spent' = 'unused';

    const recoverOnce = async (message: string) => {
      if (disposed) return;
      if (recovery === 'active') return;
      if (recovery === 'spent') {
        setError(message);
        setLoadingThread(false);
        setLoadingMessages(false);
        return;
      }
      recovery = 'active';
      unsubThread?.();
      unsubThread = null;
      unsubMessages?.();
      unsubMessages = null;
      try {
        const fresh = await loadMyThreadIds(user, { forceFresh: true });
        if (disposed) return;
        if (!fresh.value.includes(threadId)) {
          setError('This conversation is no longer available to this account.');
          setLoadingThread(false);
          setLoadingMessages(false);
          return;
        }
        subscribe();
      } catch (caught) {
        if (disposed) return;
        setError(caught instanceof Error ? caught.message : String(caught));
        setLoadingThread(false);
        setLoadingMessages(false);
      } finally {
        recovery = 'spent';
      }
    };

    const subscribe = () => {
      unsubThread = onSnapshot(
        doc(firestore, 'threads', threadId),
        (snap) => {
          if (disposed) return;
          if (snap.exists()) {
            setThread({
              id: snap.id,
              ...(snap.data() as Omit<ThreadDoc, 'id'>),
            });
          } else {
            setThread(null);
          }
          setLoadingThread(false);
        },
        (err) => {
          if (disposed) return;
          void recoverOnce(err.message);
        }
      );

      unsubMessages = onSnapshot(
        query(
          collection(firestore, 'threads', threadId, 'messages'),
          orderBy('createdAt', 'asc')
        ),
        (snap) => {
          if (disposed) return;
          setMessages(
            snap.docs.map(
              (d) => ({ id: d.id, ...(d.data() as Omit<MessageDoc, 'id'>) })
            )
          );
          setLoadingMessages(false);
        },
        (err) => {
          if (disposed) return;
          void recoverOnce(err.message);
        }
      );
    };

    void (async () => {
      try {
        // A whole-family thread can retain several server-stamped guardian or
        // class proofs, while Firestore rules inspect only index zero. The
        // directory transaction promotes a later live proof before returning
        // this id. Do that authorization handshake before either direct-link
        // listener mounts, including notification and acknowledgement links.
        // A fresh session copy of the list usually answers without a request;
        // when the copy says no, ask the server directly before telling this
        // account the conversation is gone, because the copy predates
        // conversations created moments ago (a just-opened DM most of all).
        let directory = await loadMyThreadIds(user);
        if (!directory.value.includes(threadId) && directory.served === 'cache') {
          directory = await loadMyThreadIds(user, { forceFresh: true });
        }
        if (disposed) return;
        if (!directory.value.includes(threadId)) {
          setError('This conversation is no longer available to this account.');
          setLoadingThread(false);
          setLoadingMessages(false);
          return;
        }

        subscribe();
      } catch (caught) {
        if (disposed) return;
        setError(caught instanceof Error ? caught.message : String(caught));
        setLoadingThread(false);
        setLoadingMessages(false);
      }
    })();

    return () => {
      disposed = true;
      unsubThread?.();
      unsubMessages?.();
    };
  }, [threadId, user]);

  return {
    loading: loadingThread || loadingMessages,
    thread,
    messages,
    error,
  };
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Hook returning a stable "find-or-create DM with this other user"
 * function. Looks up any existing dm thread between me and the
 * other uid, returns its id if found, otherwise creates a new one.
 *
 * Avoids the "I messaged Kim three times → three inboxes" foot-gun.
 * Note the search is one-shot (getDocs), not a live subscription, so
 * a race where both sides create a DM at the exact same millisecond
 * could produce two threads. Cosmetic, recoverable, very rare.
 *
 * ONE HUMAN, ONE CONVERSATION (Bobbie, 2026-09-07: *"The two types of jr
 * staff should act exactly the same way, zero difference other than how they
 * are set up."*). A Jr Staff can hold two live sign-ins, her dancer one and
 * her email staff account, linked by users/{uid}.studentId. A DM's id is
 * minted from two RAW uids, so the same person reached under her other uid
 * opened a SECOND inbox with the same name on it - the exact foot-gun this
 * hook exists to prevent, one layer deeper than it was looking. The target is
 * therefore resolved to the one account the lists show (her email one,
 * Bobbie's 2026-09-02 ruling) before the id is minted.
 *
 * ONLY THE RECIPIENT SIDE IS CANONICALISED ON THIS PATH, AND THE SENDER SIDE
 * USED TO BE A STOP - THIS IS THE J32 FIX FOR THAT STOP.
 *
 * Kim picking Ella mints dm_ella-email_kim every time, from every entry
 * point, which closes the split on the recipient side. But Ella herself,
 * signed in on her promoted DANCER account and picking Kim, used to mint
 * dm_ella-dancer_kim - and fetchThreadIdsFromServer could not see Kim's
 * thread either, because threadDirectory queries participantUids
 * array-contains HER signed-in uid. Kim ended up holding two DM rows under
 * one name.
 *
 * IT CANNOT BE FIXED CLIENT-SIDE, and the wall is in
 * shared/firestore.rules. Minting the id from her canonical uid while
 * keeping her real uid in participantUids fails on the READ, not the
 * create - the create really is allowed, which is why this is spelled out
 * rather than assumed: the thread that ALREADY EXISTS under her email uid
 * denies her `get`, because `allow get` requires `request.auth.uid in
 * resource.data.participantUids`. She would probe the shared id, be
 * denied, and be unable to reuse the one conversation. Putting the
 * canonical uid IN participantUids does not work either: create requires
 * `request.auth.uid in request.resource.data.participantUids`, so she
 * cannot create a thread she is not on. Naming BOTH of her uids alongside
 * Kim does not work last of all: create requires
 * `request.resource.data.participantUids.size() == 2`.
 *
 * J32'S FIX NEEDS NO RULES BYTES: a SERVER-MINTED thread (dmForJrStaff.mjs)
 * naming BOTH of her uids plus the other party. Admin SDK writes bypass the
 * size() == 2 create clause, and `allow get` is then satisfied for whichever
 * of her accounts she signs in on, because both are really in the array.
 * What would NOT work is a plain dm_<emailUid>_<other>: that leaves her
 * OTHER signed-in uid out of participantUids and the read is denied on
 * that account. So this hook checks, using the SAME directory read it
 * already waited for above, whether the CALLER (not just the target) has
 * more than one alias uid - jrStaffAliasUidsFor(user.uid, ...) - and when
 * she does, routes to the server lane instead of minting a client-side
 * stable id at all. A caller with exactly one uid (the overwhelming
 * majority of accounts) is completely unaffected: this branch never runs
 * for her, and the plain client path below is unchanged.
 *
 * THE MIGRATION NOTE, SPELLED OUT RATHER THAN GUESSED. A Jr Staff who
 * already split into two separate DM rows with the SAME other person
 * before this shipped (Kim's own case, found in the #1046 round-four
 * review) keeps both rows: dmForJrStaff.mjs finds an existing thread under
 * EITHER of her uids and returns it as-is, and deliberately does not widen
 * or merge one into the other - that is real data migration (moving or
 * combining message history), and it is Bobbie's call, not a default this
 * fix makes for her. What J32 actually closes is the FUTURE case: the
 * first DM between a Jr Staff and a given other person, from either of her
 * sign-ins, now always lands on one merged thread from the start. A
 * display-only collapse of Kim's two existing rows was considered and NOT
 * taken: it would hide a conversation that really has messages in it,
 * which is a worse failure than an honest second row.
 *
 * WHY CANONICALISE RATHER THAN REACH BOTH. A thread is readable only by the
 * uids inside its participantUids array, and shared/firestore.rules checks
 * that array directly (`allow get: if isActiveUserByDoc() && (resource ==
 * null || (request.auth.uid in resource.data.participantUids ...))`). Adding
 * her second uid would be granting read access by growing a rules-checked
 * array, and would spend a second seat of the thread cap on one person. So a
 * DM lands on ONE of her accounts, and the copy that reaches the other half
 * of her household is the guardian copy, not a second thread.
 *
 * THE DIRECTORY LISTENER, COUNTED RATHER THAN ASSUMED. This hook is used on
 * eleven surfaces; NINE of them already call useUsersList themselves
 * (NewMessage, StaffDirectory, CommandCenter, Tasks, AdminHome, AssistantChat,
 * SocialMediaReminderCard, FamilyActions, useStudioAssistant), so the query is
 * identical and Firestore serves one watch for it. Only
 * MessagePersonProfileSheet and CompBoard gain a listener they did not have,
 * and on a FAMILY account that costs nothing at all: useUsersList returns
 * [userDoc] with no snapshot for a non-staff role. It is a local listener on
 * an already-open target, not a new server stream.
 *
 * A CALLER WHO CANNOT SEE THE DIRECTORY IS UNCHANGED. useUsersList hands a
 * family account only its own document, so canonicalJrStaffAliasUid finds no
 * link and answers with the uid it was given - today's behaviour exactly,
 * never a guess about accounts this viewer is not allowed to read.
 */

/**
 * The J32 server call: find or create the one merged DM thread for a Jr
 * Staff caller (a caller who has already been proven, off the directory, to
 * hold more than one alias uid) and a target uid. Never called for anyone
 * else - useFindOrCreateDm only reaches this once it has already confirmed
 * myAliasUids.length > 1, so a plain single-account caller never spends this
 * network round trip.
 */
async function requestDmForJrStaff(user: User, targetUid: string): Promise<string> {
  let response: Response;
  try {
    response = await fetch('/.netlify/functions/dmForJrStaff', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: JSON.stringify({ targetUid }),
    });
  } catch {
    throw new Error('Could not reach the studio server. The conversation was not started.');
  }
  const payload = (await response.json().catch(() => ({}))) as {
    threadId?: string;
    error?: string;
  };
  if (!response.ok || !payload.threadId) {
    const messages: Record<string, string> = {
      sign_in_required: 'Sign in required.',
      invalid_token: 'Sign in required.',
      jr_staff_alias_required: "Can't start that conversation right now. Refresh and try again.",
      cant_dm_self: "Can't DM yourself.",
      target_not_found: 'That person could not be found. Refresh and try again.',
      bad_request: 'That conversation could not be started. Refresh and try again.',
      server_not_configured: 'The studio message server is not configured yet.',
      'missing app check token': 'This device could not be verified. Refresh and try again.',
      'invalid app check token': 'This device could not be verified. Refresh and try again.',
      too_many_requests: 'Too many messages started too quickly. Wait a bit and try again.',
    };
    throw new Error(messages[payload.error ?? ''] ?? 'That conversation could not be started.');
  }
  return payload.threadId;
}

export function useFindOrCreateDm() {
  const { user } = useAuth();
  const { users, loading: usersLoading } = useUsersList();
  // Read through a ref, so the returned function sees the directory as it is
  // WHEN IT RUNS rather than as it was when the component last rendered.
  const directoryRef = useRef<{ users: UserDoc[]; loading: boolean }>({ users, loading: usersLoading });
  directoryRef.current = { users, loading: usersLoading };
  return async (otherUid: string): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    if (otherUid === user.uid) {
      throw new Error("Can't DM yourself.");
    }
    // WAIT FOR THE DIRECTORY, OR MINT THE SPLIT THIS FUNCTION EXISTS TO STOP.
    // useUsersList returns [] while its snapshot is in flight, and an empty
    // list makes canonicalJrStaffAliasUid answer with the raw uid - the exact
    // pre-fix behaviour. A DM opened in that window (a tap straight off a
    // notification, a deep link, a fast click on a freshly mounted screen)
    // would mint dm_<raw>, and the same conversation would then exist under
    // two ids forever.
    //
    // SO IT WAITS RATHER THAN REFUSING. A fast tap is the COMMON case on
    // these eight surfaces, and throwing at somebody who tapped promptly
    // would be punishing them for being quick. The listener normally answers
    // in well under a second; this gives it a bounded moment and only then
    // gives up, because a split thread is unrecoverable and a retry is not.
    const directory = await settledDirectory(directoryRef);
    if (directory.loading) {
      throw new Error('Still loading the studio directory. Try that again in a moment.');
    }
    const target = canonicalJrStaffAliasUid(otherUid, directory.users) || otherUid;
    // Her own other sign-in is still herself. Without this, a Jr Staff on her
    // dancer account could open a DM "with" her email account and talk to
    // nobody - the raw-uid check above cannot see that they are one person.
    if (target === user.uid
      || canonicalJrStaffAliasUid(user.uid, directory.users) === target) {
      throw new Error("Can't DM yourself.");
    }

    // J32: A CALLER WITH MORE THAN ONE ALIAS UID ROUTES TO THE SERVER LANE.
    // jrStaffAliasUidsFor answers off the SAME directory read this function
    // already waited for above - never a second listener - and needs her own
    // studentId to find her linked pair, read from her own entry in that
    // directory when present. Anyone with exactly one alias uid (everyone
    // who is not a Jr Staff holding both a dancer sign-in and an email
    // account) gets exactly today's behaviour below, unchanged.
    const myAliasUids = jrStaffAliasUidsFor({
      uid: user.uid,
      studentId: directory.users.find((account) => account.uid === user.uid)?.studentId ?? null,
      accounts: directory.users,
    });
    if (myAliasUids.length > 1) {
      return requestDmForJrStaff(user, target);
    }

    const stableId = dmThreadId(user.uid, target);
    const stableRef = doc(db, 'threads', stableId);
    const stableSnap = await getDoc(stableRef);
    if (stableSnap.exists()) return stableId;

    // Deliberately uncached: this filtered lookup is rare, user-initiated,
    // and must reflect the server's current answer before creating a thread.
    const existingIds = await fetchThreadIdsFromServer(user, { ordinaryOnly: true, otherUid: target });
    if (existingIds[0]) return existingIds[0];

    const participants = [user.uid, target].sort();
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(stableRef);
      if (snap.exists()) return;
      tx.set(stableRef, {
        participantUids: participants,
        kind: 'dm' as const,
        title: null,
        lastMessageText: null,
        lastMessageAt: null,
        lastMessageBy: null,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
      });
    });
    return stableId;
  };
}

export interface NewGroupInput {
  title: string;
  otherUids: string[]; // excludes the creator — we add them automatically
}

/**
 * Create a new group thread. Unlike findOrCreateDm there's no dedup —
 * a group is uniquely identified by Bobbie's intent, not by its
 * membership, and "let me have a separate group with the same five
 * people for a different topic" is a valid use case.
 *
 * SERVER OWNED (Codex round 8 of #796): the browser supplies only a title
 * and who else is in the room. Every participant label and mention handle
 * is derived server side (netlify/functions/lib/threadGroupCreate.mjs)
 * from data only the server reads, then written with the Admin SDK -
 * shared/firestore.rules' staff group-create branch was replaced by
 * refusal in the same round, so a client can no longer stamp identity onto
 * a group thread at all. This closes the impersonation hole a
 * client-stamped participantLabels design left open: a stale, custom, or
 * compromised staff client could otherwise legally stamp any string for
 * any roster uid, including its own, and a family viewer (who cannot read
 * the directory to catch it) would see it forever.
 *
 * The cap check here is a courtesy that saves a doomed round trip, not the
 * real gate - the server function's own MAX_GROUP_PARTICIPANTS check is
 * (see lib/threadLimits.ts for the full lockstep story). Same story for the
 * caller-strip-and-dedupe below (Codex round 9, finding P2): the server
 * sanitizes otherUids itself, this is a courtesy that keeps a stale
 * client's own request well-formed before it is even sent.
 *
 * NO STAND-IN CLAIM (Codex round 11, finding P1, replacing rounds 9 and
 * 10's input.standInUids entirely): a Jr Staff guardian stand-in's "Parent
 * of <dancer>" label is now derived server side from the PARTICIPANT'S OWN
 * account role, for every family-role participant, unconditionally - never
 * from anything this hook sends. See netlify/functions/lib/
 * threadGroupCreate.mjs's module doc comment for the full weakest-reader
 * reasoning: the stamp is global (every participant reads it), so which
 * label a family participant gets can never depend on who created the
 * group or what that caller happened to claim.
 */
export function useCreateGroupThread() {
  const { user } = useAuth();
  return async (input: NewGroupInput): Promise<string> => {
    if (!user) throw new Error('Sign in required.');
    const otherUids = Array.from(
      new Set(input.otherUids.filter((uid) => uid && uid !== user.uid))
    );
    if (otherUids.length + 1 < 3) {
      throw new Error('A group needs at least 3 people (including you).');
    }
    if (otherUids.length > MAX_GROUP_OTHERS) {
      throw new Error(`A group thread tops out at ${MAX_GROUP_OTHERS} people plus you.`);
    }
    const title = input.title.trim();
    if (!title) throw new Error('Give the group a title.');
    if (title.length > 120) {
      throw new Error('Keep the group title to 120 characters or fewer.');
    }

    let response: Response;
    try {
      response = await fetch('/.netlify/functions/threadGroupCreate', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: await functionHeaders(user),
        body: JSON.stringify({ title, otherUids }),
      });
    } catch {
      throw new Error('Could not reach the studio server. The group was not started.');
    }
    const payload = (await response.json().catch(() => ({}))) as {
      threadId?: string;
      error?: string;
    };
    if (!response.ok || !payload.threadId) {
      const messages: Record<string, string> = {
        sign_in_required: 'Sign in required.',
        invalid_token: 'Sign in required.',
        active_staff_required: 'Only active staff can start a group.',
        too_few_participants: 'A group needs at least 3 people (including you).',
        group_too_large: `A group thread tops out at ${MAX_GROUP_OTHERS} people plus you.`,
        participant_invalid:
          'One of the people you picked is no longer reachable. Refresh and try again.',
        bad_request: 'That group could not be started. Check your picks and try again.',
        server_not_configured: 'The studio message server is not configured yet.',
        'missing app check token': 'This device could not be verified. Refresh and try again.',
        'invalid app check token': 'This device could not be verified. Refresh and try again.',
        too_many_requests: 'Too many groups started too quickly. Wait a bit and try again.',
      };
      throw new Error(messages[payload.error ?? ''] ?? 'That group could not be started.');
    }
    return payload.threadId;
  };
}

/**
 * Remove the current user from a thread's participant list. Works for
 * both DMs and groups — once you leave you can't read further messages,
 * and findOrCreateDm() will spin up a fresh thread next time someone
 * starts a DM with you. Rules enforce that the write only removes
 * yourself, not anyone else.
 */
export function useLeaveThread() {
  const { user } = useAuth();
  return async (
    threadId: string,
    currentParticipants: string[]
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const next = currentParticipants.filter((u) => u !== user.uid);
    await updateDoc(doc(db, 'threads', threadId), {
      participantUids: next,
    });
  };
}

export interface MentionHit {
  threadId: string;
  message: MessageDoc;
}

export interface UseMySentMessagesResult {
  loading: boolean;
  hits: MentionHit[];
  error: string | null;
}

interface SentMessageResult {
  ownerUid: string | null;
  hits: MentionHit[];
}

function timestampVersion(value: ThreadDoc['lastMessageAt']): number {
  const candidate = value as { toMillis?: () => number } | null;
  return candidate?.toMillis?.() ?? 0;
}

/**
 * Load the signed-in person's own chat messages across the conversations
 * they can currently read. This runs only while the Sent view is open.
 *
 * Each read uses the same per-thread messages collection that the regular
 * conversation screen already uses. That keeps the query compatible with
 * the deployed participant rules and avoids adding a collection-group index
 * or a wider read path just for this view.
 */
export function useMySentMessages(
  threads: readonly ThreadDoc[],
  enabled: boolean
): UseMySentMessagesResult {
  const { user } = useAuth();
  const [result, setResult] = useState<SentMessageResult>({
    ownerUid: null,
    hits: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ownerUid = useRef<string | null>(null);
  const generation = useRef(0);
  const hitsByThread = useRef(new Map<string, MentionHit[]>());
  const loadedVersions = useRef(new Map<string, number>());
  const queuedVersions = useRef(new Map<string, number>());
  const inFlightVersions = useRef(new Map<string, number>());
  const failedVersions = useRef(new Map<string, number>());
  const currentVersions = useRef(new Map<string, number>());
  const activeReads = useRef(0);
  const pumpReads = useRef<() => void>(() => undefined);
  const versionRows: SentThreadVersion[] = threads.map((thread) => ({
    id: thread.id,
    version: timestampVersion(thread.lastMessageAt),
  }));
  const signature = user
    ? `${user.uid}:${versionRows
        .map(({ id, version }) => `${id}:${version}`)
        .sort()
        .join('|')}`
    : '';

  useEffect(() => {
    const firestore = db;
    if (!firestore || !user) {
      generation.current += 1;
      ownerUid.current = null;
      hitsByThread.current.clear();
      loadedVersions.current.clear();
      queuedVersions.current.clear();
      inFlightVersions.current.clear();
      failedVersions.current.clear();
      currentVersions.current.clear();
      pumpReads.current = () => undefined;
      setResult({ ownerUid: null, hits: [] });
      setError(null);
      setLoading(false);
      return;
    }

    const uid = user.uid;
    if (ownerUid.current !== uid) {
      generation.current += 1;
      ownerUid.current = uid;
      hitsByThread.current.clear();
      loadedVersions.current.clear();
      queuedVersions.current.clear();
      inFlightVersions.current.clear();
      failedVersions.current.clear();
      currentVersions.current.clear();
      setResult({ ownerUid: null, hits: [] });
      setError(null);
    }

    const requestGeneration = generation.current;
    const activeVersions = new Map(versionRows.map(({ id, version }) => [id, version]));
    currentVersions.current = activeVersions;

    const publish = () => {
      if (generation.current !== requestGeneration || ownerUid.current !== uid) return;
      const next = Array.from(hitsByThread.current.values()).flat().sort((a, b) => {
        const aMs = (a.message.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
        const bMs = (b.message.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
        return bMs - aMs;
      });
      // Store the owner and rows together. The hook never returns rows whose
      // owner differs from the current auth user, including the render before
      // this effect cleans up after an account switch on a shared device.
      setResult({ ownerUid: uid, hits: next });
      setError(
        failedVersions.current.size > 0
          ? 'Sent messages could not be loaded. Please try again.'
          : null
      );
    };

    let pruned = false;
    for (const id of hitsByThread.current.keys()) {
      if (!activeVersions.has(id)) {
        hitsByThread.current.delete(id);
        loadedVersions.current.delete(id);
        queuedVersions.current.delete(id);
        inFlightVersions.current.delete(id);
        failedVersions.current.delete(id);
        pruned = true;
      }
    }
    for (const [id, failedVersion] of failedVersions.current) {
      if (activeVersions.get(id) !== failedVersion) {
        failedVersions.current.delete(id);
      }
    }
    if (pruned) publish();

    if (!enabled) {
      // Reopening Sent is an explicit refresh. Keep the visible rows for a
      // progressive return, but require fresh server reads for every thread.
      loadedVersions.current.clear();
      queuedVersions.current.clear();
      pumpReads.current = () => undefined;
      setLoading(false);
      return;
    }

    setError(null);
    for (const { id, version } of sentThreadReadPlan(
      versionRows,
      loadedVersions.current,
      queuedVersions.current,
      inFlightVersions.current
    )) {
      queuedVersions.current.set(id, version);
    }

    const pump = () => {
      if (generation.current !== requestGeneration || ownerUid.current !== uid) return;

      while (
        activeReads.current < MAX_CONCURRENT_SENT_READS &&
        queuedVersions.current.size > 0
      ) {
        const queued = queuedVersions.current.entries().next().value as
          | [string, number]
          | undefined;
        if (!queued) break;
        const [threadId, version] = queued;
        queuedVersions.current.delete(threadId);
        if (
          currentVersions.current.get(threadId) !== version ||
          loadedVersions.current.get(threadId) === version ||
          inFlightVersions.current.get(threadId) === version
        ) {
          continue;
        }

        inFlightVersions.current.set(threadId, version);
        activeReads.current += 1;
        void getDocsFromServer(
          query(
            collection(firestore, 'threads', threadId, 'messages'),
            where('authorUid', '==', uid)
          )
        )
          .then((snap) => {
            if (
              generation.current !== requestGeneration ||
              ownerUid.current !== uid ||
              currentVersions.current.get(threadId) !== version
            ) {
              return;
            }
            hitsByThread.current.set(
              threadId,
              snap.docs.map((messageDoc) => ({
                threadId,
                message: {
                  id: messageDoc.id,
                  ...(messageDoc.data() as Omit<MessageDoc, 'id'>),
                },
              }))
            );
            loadedVersions.current.set(threadId, version);
            failedVersions.current.delete(threadId);
            publish();
          })
          .catch((reason: unknown) => {
            if (
              generation.current !== requestGeneration ||
              ownerUid.current !== uid ||
              currentVersions.current.get(threadId) !== version
            ) {
              return;
            }
            console.error('useMySentMessages: server read failed', reason);
            failedVersions.current.set(threadId, version);
            publish();
          })
          .finally(() => {
            activeReads.current = Math.max(0, activeReads.current - 1);
            if (
              generation.current === requestGeneration &&
              ownerUid.current === uid &&
              inFlightVersions.current.get(threadId) === version
            ) {
              inFlightVersions.current.delete(threadId);
              const latestVersion = currentVersions.current.get(threadId);
              if (
                latestVersion !== undefined &&
                loadedVersions.current.get(threadId) !== latestVersion &&
                queuedVersions.current.get(threadId) !== latestVersion
              ) {
                // Activity can change while this thread is being read. Keep
                // only one read in flight per thread, then coalesce every
                // intervening change into one refresh of the latest version.
                queuedVersions.current.set(threadId, latestVersion);
              }
              setLoading(
                queuedVersions.current.size > 0 || inFlightVersions.current.size > 0
              );
            }
            pumpReads.current();
          });
      }

      setLoading(
        queuedVersions.current.size > 0 || inFlightVersions.current.size > 0
      );
      if (queuedVersions.current.size === 0 && inFlightVersions.current.size === 0) {
        publish();
      }
    };

    pumpReads.current = pump;
    pump();
  }, [enabled, signature, user]);

  useEffect(() => () => {
    generation.current += 1;
    queuedVersions.current.clear();
    pumpReads.current = () => undefined;
  }, []);

  const resultBelongsToUser = Boolean(user && result.ownerUid === user.uid);
  return {
    loading: loading || Boolean(enabled && user && !resultBelongsToUser),
    hits: resultBelongsToUser ? result.hits : [],
    error: resultBelongsToUser ? error : null,
  };
}

export interface UseMyMentionsResult {
  loading: boolean;
  hits: MentionHit[];
  error: string | null;
}
/**
 * Collection-group query across every messages subcollection for
 * messages that @-mention the signed-in user, newest first. Drives
 * the Messages inbox so recent callouts stay in the same feed without combing
 * each thread by hand.
 *
 * Backed by the composite index in shared/firestore.indexes.json.
 * Rules: each match doc is read-allowed only if I'm a participant of
 * the parent thread — which I always am if I was @-mentioned (you
 * can only mention thread participants from the composer picker).
 */
export function useMyMentions(): UseMyMentionsResult {
  const { user } = useAuth();
  const [hits, setHits] = useState<MentionHit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A failed listener rebuilds when the person comes back to the app or the
  // network returns (lib/listenerRevival.ts), and the success path below
  // clears the error, so a revived listener actually reads as healthy.
  const revival = useReviveAfterError(error !== null);

  useEffect(() => {
    if (!db || !user) {
      setHits([]);
      setLoading(false);
      return;
    }
    const q = query(
      collectionGroup(db, 'messages'),
      where('mentions', 'array-contains', user.uid),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setHits(
          snap.docs.map((d) => {
            // path looks like threads/{threadId}/messages/{messageId}
            const parts = d.ref.path.split('/');
            const threadId = parts[1] ?? '';
            return {
              threadId,
              message: {
                id: d.id,
                ...(d.data() as Omit<MessageDoc, 'id'>),
              },
            };
          })
        );
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user, revival]);

  return { loading, hits, error };
}

export interface UseMyUnackedResult {
  loading: boolean;
  /** Must-read messages I'm a participant of where I haven't acked yet. */
  unacked: MentionHit[];
  /**
   * Non-null only when the listener itself failed (a rules or index
   * problem on this collection-group query, which has broken in
   * production before). Added for the honesty sweep: this used to swallow
   * the error entirely and settle on unacked=[], loading=false - a
   * consumer showing a confident "All clear, nice" off an empty unacked
   * list, like the home attention card, must treat error!=null as "do not
   * know," never as zero. Mirrors useMyMentions' identical field on the
   * same collection-group query shape, a few lines above.
   */
  error: string | null;
  /**
   * True when a list was FULL: more than MUST_READ_SCAN_CAP messages name me
   * as a must-read, or the family must-read page said more are waiting than
   * it returned. Nothing failed, but an older must-read could sit past the
   * edge, so the home row does not say "All clear, nice" on a zero while this
   * is true (Bobbie, 2026-09-29, "it says i'm all caught up but I have not
   * read some of the messages"). It is NOT a failure: the bell, the tile and
   * the badge show the number found. See WaitingCountState.
   */
  capped: boolean;
}

/**
 * HOW FAR BACK A MUST-READ IS LOOKED FOR, AND WHY IT IS ONE WINDOW.
 *
 * This read the newest 50 messages naming me in expectedAckUids. Must-reads
 * I already acknowledged fill that list too (Firestore cannot filter on "my
 * key is missing from acks", so they are dropped only after the read), so
 * once 50 newer must-reads existed an older one I never answered fell off the
 * end and the home card said "All clear, nice". Reproduced on the whole-app
 * harness on 2026-09-29: 1 old unanswered plus 49 newer answered read "1
 * must-read"; with 50 newer it read all clear.
 *
 * The window is now 200. It asks for ONE MORE than that, so it can tell "more
 * than 200 exist" (`capped`) from "exactly 200 exist" (complete): a window
 * that came back with exactly 200 saw everything. It is ONE live query rather
 * than pages walked with startAfter, on purpose:
 *   - Firestore bills the documents a query returns, not its limit, so a
 *     person with 12 must-reads pays 12 reads either way. Only someone who
 *     really has more than 50 pays more, and she pays each document once.
 *   - A page walk re-reads its pages whenever a new must-read arrives (the
 *     cursor moves) and, done with getDocs, bills once per mounted copy of
 *     this hook (the home card, the ticker, the bell and the badge each mount
 *     one), where identical live queries share one listen target.
 *   - A live window also sees her Understood land on an OLD must-read, so the
 *     count drops the moment she answers it; a one-shot page would not.
 *
 * `capped` is NOT a failure. A full window is usually full of must-reads she
 * already answered, and it stays full; see WaitingCountState in
 * staffWaitingOnYouCore.ts for the only thing it changes (the home row's
 * zero line). The real fix is a field that lists only the people who still
 * owe an acknowledgement (removed in the same write as the ack), so the
 * query itself can skip answered ones. That is a new stored field and rules
 * bytes, so it waits for Bobbie's go.
 */
export const MUST_READ_SCAN_CAP = 200;

/**
 * ONE FAMILY ATTENTION CALL FOR THE WHOLE PAGE, NOT ONE PER COUNTER.
 *
 * Bobbie, 2026-10-22, on her phone: the staff home's Waiting on you row said
 * "Could not check messages. Try again." and Try again did not clear it.
 * useMyUnacked is mounted by the home card, the ticker, the bell, the phone
 * badge and the tile counts (home and menu), and each copy called
 * familyAttention on mount, every 30 seconds, on focus and on every return
 * to the app. Six copies at two a minute is the endpoint's whole budget (120
 * GETs per 10 minutes per account, charged before the guardian check, so a
 * Director with no family messages pays it too), and coming back to the app
 * pushed it over: a 429, which the row honestly reports as a failed check,
 * and a reload only spent more of the same window. The answer is the same
 * for every copy, so they now share it through the same session cache the
 * thread directory uses: one request per freshness window, concurrent calls
 * joined, a failure never cached. Fresh just under the 30 second poll, so
 * each tick still does real work. An acknowledgement forces a fresh read.
 */
const FAMILY_ATTENTION_FRESH_MS = 25_000;
const familyAttentionCache = createDirectoryCache<FamilyAttentionPage>(FAMILY_ATTENTION_FRESH_MS);

function loadFamilyAttention(
  user: User,
  options: { forceFresh?: boolean } = {}
): Promise<DirectoryCacheLoad<FamilyAttentionPage>> {
  return familyAttentionCache.load(user.uid, () => listFamilyAttention({ user }), options);
}

/**
 * Collection-group scan for messages someone marked ack-required that
 * I haven't acked. Filters happen client-side: I'm a participant of
 * the parent thread AND I'm not the author AND acks[my uid] is empty.
 *
 * The wrapping rule already restricts the Firestore read to messages
 * in threads I'm a participant of, so we're not leaking visibility.
 * The client filter is just to remove false positives (e.g. my own
 * ack-required messages don't need to flag MYSELF), and it lives in
 * staffWaitingOnYouCore's messageStillNeedsMyAck so it can be tested.
 */
export function useMyUnacked(): UseMyUnackedResult {
  const { user } = useAuth();
  const [ordinaryUnacked, setOrdinaryUnacked] = useState<MentionHit[]>([]);
  const [ordinaryLoading, setOrdinaryLoading] = useState(true);
  const [ordinaryError, setOrdinaryError] = useState<string | null>(null);
  const [ordinaryCapped, setOrdinaryCapped] = useState(false);
  const [familyUnacked, setFamilyUnacked] = useState<MentionHit[]>([]);
  const [familyLoading, setFamilyLoading] = useState(true);
  const [familyError, setFamilyError] = useState<string | null>(null);
  const [familyHasMore, setFamilyHasMore] = useState(false);
  // See useMyMentions: a dead listener rebuilds when she comes back.
  const ordinaryRevival = useReviveAfterError(ordinaryError !== null);

  useEffect(() => {
    if (!db || !user) {
      setOrdinaryUnacked([]);
      setOrdinaryLoading(false);
      setOrdinaryError(null);
      setOrdinaryCapped(false);
      return;
    }
    // See MUST_READ_SCAN_CAP above for why this is one bounded window.
    const q = query(
      collectionGroup(db, 'messages'),
      where('expectedAckUids', 'array-contains', user.uid),
      orderBy('createdAt', 'desc'),
      limit(MUST_READ_SCAN_CAP + 1)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const me = user.uid;
        const out: MentionHit[] = [];
        for (const d of snap.docs) {
          const data = d.data() as Omit<MessageDoc, 'id'>;
          // The rule lives in staffWaitingOnYouCore so it can be tested
          // against a real filing: nothing but a stamped acknowledgement
          // clears a must-read, which is why the surfaces that count it say
          // MUST READ rather than UNREAD.
          if (!messageStillNeedsMyAck(data, me)) continue;
          const parts = d.ref.path.split('/');
          const threadId = parts[1] ?? '';
          out.push({
            threadId,
            message: { id: d.id, ...data },
          });
        }
        setOrdinaryUnacked(out);
        // MORE than the window means an older must-read could sit past its
        // edge. Exactly MUST_READ_SCAN_CAP is the whole list.
        setOrdinaryCapped(snap.docs.length > MUST_READ_SCAN_CAP);
        setOrdinaryError(null);
        setOrdinaryLoading(false);
      },
      (err) => {
        setOrdinaryUnacked([]);
        setOrdinaryCapped(false);
        setOrdinaryError(err.message);
        setOrdinaryLoading(false);
      }
    );
    return () => unsub();
  }, [user, ordinaryRevival]);

  useEffect(() => {
    if (!user) {
      setFamilyUnacked([]);
      setFamilyLoading(false);
      setFamilyError(null);
      setFamilyHasMore(false);
      return;
    }
    let current = true;
    let inFlight = false;
    const refresh = async (forceFresh = false) => {
      if (inFlight) return;
      inFlight = true;
      try {
        const { value: page } = await loadFamilyAttention(user, { forceFresh });
        if (!current) return;
        setFamilyUnacked(page.items.map((item) => ({
          threadId: item.threadId,
          message: {
            id: item.messageId,
            authorUid: '',
            text: item.text,
            mentions: [],
            ackRequired: true,
            expectedAckUids: [],
            familyExpectedAckUids: [user.uid],
            acks: {},
            createdAt: Timestamp.fromMillis(Date.parse(item.createdAt)),
          },
        })));
        // MORE WAITING IS A FULL LIST, NOT A FAILED ONE. This used to be
        // reported as an error string, which a surface can only render as
        // "could not check". It is really "at least this many", the same
        // edge as a full must-read window, so it travels as `capped`.
        setFamilyHasMore(page.hasMore);
        setFamilyError(null);
      } catch (caught) {
        if (!current) return;
        setFamilyError(caught instanceof Error ? caught.message : String(caught));
      } finally {
        if (current) setFamilyLoading(false);
        inFlight = false;
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    // An acknowledgement just changed the list, so this one skips the cache.
    const refreshAfterAck = () => {
      if (document.visibilityState === 'visible') void refresh(true);
    };
    const timer = window.setInterval(() => void refresh(), 30_000);
    window.addEventListener('focus', refreshWhenVisible);
    window.addEventListener(FAMILY_ATTENTION_CHANGED_EVENT, refreshAfterAck);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    void refresh();
    return () => {
      current = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshWhenVisible);
      window.removeEventListener(FAMILY_ATTENTION_CHANGED_EVENT, refreshAfterAck);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [user]);

  const unacked = useMemo(() => {
    const byMessage = new Map<string, MentionHit>();
    for (const hit of ordinaryUnacked) byMessage.set(`${hit.threadId}/${hit.message.id}`, hit);
    for (const hit of familyUnacked) byMessage.set(`${hit.threadId}/${hit.message.id}`, hit);
    return [...byMessage.values()].sort((a, b) => {
      const aMillis = (a.message.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
      const bMillis = (b.message.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
      return bMillis - aMillis || a.threadId.localeCompare(b.threadId)
        || a.message.id.localeCompare(b.message.id);
    });
  }, [ordinaryUnacked, familyUnacked]);
  const error = [ordinaryError, familyError].filter(Boolean).join(' ') || null;
  return {
    loading: ordinaryLoading || familyLoading,
    unacked,
    error,
    capped: ordinaryCapped || familyHasMore,
  };
}

/**
 * Send a message to a thread + update the thread's denormalized
 * lastMessage* fields in the same flow. We don't wrap these in a batch
 * because writes to different docs aren't atomic across collections and
 * the thread metadata being slightly stale is fine — the message itself
 * is the source of truth.
 *
 * Text plus optional image/GIF attachments. Empty sends are rejected unless
 * there is at least one attachment.
 */
export interface SendMessageOptions {
  mentions?: string[];
  ackRequired?: boolean;
  attachments?: MessageAttachment[];
  /**
   * LAYER 2 of the round-4 fix on the Jr Staff board mirror (notify.mjs's
   * resolveJrStaffBoardMirrorExclusion carries the real, server-side proof
   * now - this is belt and suspenders for the honest-failure case only).
   * Called once, right before this function returns, with whether every
   * notifyUsers call this send actually made reported real delivery
   * (`serverReached` true and `inApp` greater than zero - see
   * NotifyDeliveryResult in notifications.ts). NewMessage.tsx's Jr Staff
   * group mirror uses this to decide whether it is safe to cite
   * mirrorOfThreadId/mirrorOfMessageId at all: on a failed or zeroed
   * result, the mirror still posts, but with no citation, so the board
   * rings the whole board instead of trusting a send that may never have
   * reached anyone. Unset for every other caller of useSendMessage - zero
   * behavior change there.
   */
  onMessageNotifyResult?: (delivered: boolean) => void;
  /**
   * Set ONLY by the parents-only class message (lib/classParentsMessage.ts)
   * on the group's opening message, so the parents are told the way a class
   * post tells them. See ThreadNotifyInput.classAnnouncement. The notify
   * lane still reaches thread participants and nobody else.
   */
  classAnnouncement?: boolean;
}

function cleanAttachmentName(name: string): string {
  return (name.trim() || 'image').slice(0, 160);
}

function normalizeMessageAttachments(
  attachments: MessageAttachment[] | undefined
): MessageAttachment[] {
  return (attachments ?? [])
    .slice(0, MAX_MESSAGE_ATTACHMENTS)
    .map((a) => {
      const contentType = (a.contentType || 'image/*').slice(0, 120);
      // kind follows the actual content type instead of being hardcoded -
      // shared/firestore.rules hasMessageAttachmentShape() enforces that
      // kind and contentType AGREE, so deriving one from the other here is
      // what keeps every send inside the shape.
      const kind = contentType.startsWith('video/')
        ? ('video' as const)
        : contentType.startsWith('image/')
          ? ('image' as const)
          : ('file' as const);
      return {
        id: a.id.slice(0, 80),
        kind,
        url: a.url.slice(0, 2048),
        name: cleanAttachmentName(a.name),
        contentType,
        ...(a.storagePath ? { storagePath: a.storagePath.slice(0, 1000) } : {}),
      };
    })
    .filter(
      (a) =>
        a.id &&
        a.url &&
        (a.contentType.startsWith('image/') ||
          a.contentType.startsWith('video/') ||
          isMessageFileContentType(a.contentType))
    );
}

/**
 * Some browsers/OSes leave File.type blank for common photo and video
 * formats picked off a phone (HEIC photos and some camera-roll videos
 * especially) - the same disease that made extensionless .m4a uploads
 * report empty type elsewhere in the app (see inferContentType in
 * lib/tasks.ts). Infer from the file extension when the browser gives us
 * nothing, so a real photo or video is not rejected as "not an image or
 * video" just because the browser did not label it.
 */
const ATTACHMENT_EXTENSION_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/mp4',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  '3gp': 'video/3gpp',
  // Plain files (the message-attachments 'file' lane). m4a especially:
  // the blank-File.type disease documented above hits audio hardest.
  pdf: 'application/pdf',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  flac: 'audio/flac',
  aif: 'audio/aiff',
  aiff: 'audio/aiff',
  txt: 'text/plain',
  csv: 'text/csv',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function inferAttachmentContentType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ATTACHMENT_EXTENSION_MIME[ext] || '';
}

/**
 * Any active thread participant (family or staff) can attach an image, GIF,
 * video, or plain file (PDF, audio, text/CSV, Word/Excel - see
 * isMessageFileContentType). Name kept as-is
 * (useUploadMessageImageAttachment) to avoid touching the other callers of
 * this hook (useStudioAssistant.ts, CommandCenter.tsx), which only ever
 * feed it photos; the behavior underneath covers video and files too.
 */
function threadMessageAttachmentPath(
  threadId: string,
  uid: string,
  id: string,
  safeName: string,
): string {
  return `message-attachments/${threadId}/${uid}/${id}-${safeName}`;
}

function pendingFamilyMessageAttachmentPath(
  _scopeId: string,
  uid: string,
  id: string,
  safeName: string,
): string {
  return `family-message-attachments/${uid}/${id}-${safeName}`;
}

function safeStorageName(name: string): string {
  return (name || 'attachment').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
}

function useUploadMessageAttachmentAtPath(
  pathForUpload: (scopeId: string, uid: string, id: string, safeName: string) => string,
  // Two different lanes share this factory, gated by two different
  // mechanisms even though both now cap at 6 GB: the gateway lane (server-
  // enforced, raised 2026-08-18) and the direct-Storage pending-family lane
  // (enforced by shared/storage.rules' isMessageVideoUpload(), raised to
  // match in the same change). Kept as two constants so the lanes can
  // diverge again without hunting down every caller.
  videoMaxBytes: number,
) {
  const { user } = useAuth();
  return async (
    scopeId: string,
    file: File,
    onProgress?: (fraction: number) => void,
    // The composer draft's stable id, threaded through to the gateway below so
    // a second Send re-enters the same reservation instead of charging the
    // sender's byte quota for the same video again. Only the gateway branch
    // can use it; the pending-family path writes to Storage directly.
    requestId?: string,
  ): Promise<MessageAttachment> => {
    if (!user) throw new Error('Sign in required.');
    const inferredType = inferAttachmentContentType(file);
    const isVideo = inferredType.startsWith('video/');
    const isImage = inferredType.startsWith('image/');
    const isFile = !isVideo && !isImage && isMessageFileContentType(inferredType);
    if (!isImage && !isVideo && !isFile) {
      throw new Error(
        'Choose a photo, GIF, video, PDF, music file, or document (Word/Excel/CSV) from your phone.'
      );
    }
    if (isVideo && file.size >= videoMaxBytes) {
      const gb = Math.round(videoMaxBytes / (1024 * 1024 * 1024));
      throw new Error(`Videos need to be under ${gb} GB. For anything longer, share a Drive or YouTube link.`);
    }
    if (isFile && file.size >= MESSAGE_FILE_ATTACHMENT_MAX_BYTES) {
      throw new Error('Files need to be under 25 MB.');
    }

    // Photos take the same shrink pipeline as class photos (downscaleImage in
    // lib/offerings.ts). Before this, a full-resolution phone shot got a flat
    // "Photos need to be under 8 MB." refusal - Ms. Kim's exact "I'm trying
    // and it's not working" - where every other photo surface in the Hub
    // quietly re-encodes to a ~2000px JPEG well under the Storage cap. The
    // same pass converts iPhone HEIC to JPEG, so an attached photo displays
    // on every participant's device, not just Apple ones. GIFs skip it
    // (re-encoding freezes the animation) and keep the plain cap instead.
    let body: Blob = file;
    let contentType = inferredType;
    if (isImage && inferredType !== 'image/gif') {
      // Passing inferredType matters for HEIC twice over: it lets the
      // pipeline keep the converted JPEG even when the re-encode came out
      // larger than the (very compact) HEIC original, and it covers files
      // whose browser-reported type is blank.
      body = await downscaleImage(file, inferredType);
      if (body !== file) contentType = 'image/jpeg';
    }
    if (/^image\/hei[cf]$/.test(contentType)) {
      // downscaleImage returned the file untouched, and for a HEIC source
      // that only happens when THIS browser cannot decode HEIC at all
      // (desktop Chrome, mostly). Uploading it raw would render as a blank
      // box for every non-Apple viewer - refuse with the fix instead of
      // sending a photo half the studio cannot see.
      throw new Error(
        "That photo is in Apple's HEIC format, which this browser can't convert. Export it as a JPEG or PNG and try again."
      );
    }
    if (isImage && body.size >= MESSAGE_IMAGE_ATTACHMENT_MAX_BYTES) {
      throw new Error(
        inferredType === 'image/gif'
          ? 'GIFs need to be under 8 MB.'
          : 'That photo is too large even after shrinking. Pick one under 8 MB.'
      );
    }

    if (pathForUpload === threadMessageAttachmentPath) {
      // Existing threads always use the server control plane. That enforces
      // live membership, App Check, byte-signature validation, immutable
      // objects, and per-account upload quotas.
      const { uploadAttachmentViaGateway } = await loadAttachmentGateway();
      const uploaded = await uploadAttachmentViaGateway({
        target: { kind: 'thread', threadId: scopeId },
        source: {
          blob: body,
          name: file.name || (isVideo ? 'Video' : isFile ? 'File' : 'Photo'),
          contentType,
        },
        user,
        onProgress,
        requestId,
      });
      return {
        id: uploaded.id,
        kind: uploaded.kind,
        url: uploaded.url,
        name: uploaded.name,
        contentType: uploaded.contentType,
        storagePath: uploaded.storagePath,
      };
    }

    // A first whole-family message has no thread id yet. Stage its attachment
    // in the active staff sender's own narrowly ruled folder; familyMessage
    // verifies this exact prefix and the tokenized URL before committing it.
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const safeName = safeStorageName(file.name);
    const uploadPath = pathForUpload(scopeId, user.uid, id, safeName);
    if (!storage) throw new Error('File storage is not initialized.');
    const ref = storageRef(storage, uploadPath);
    try {
      // Storage's rule reads request.resource.contentType, so we must upload
      // with the INFERRED (post-conversion) type, not the (possibly blank)
      // file.type - an upload sent with an empty contentType fails
      // isImageUpload() / isMessageVideoUpload() and comes back as
      // storage/unauthorized, which reads like a permissions bug instead of
      // the MIME-guessing gap it is.
      if (isVideo || body.size > QUICK_UPLOAD_MAX_BYTES) {
        // Resumable + progress for anything legitimately slow: a solo clip
        // takes minutes on studio Wi-Fi, and a near-8MB GIF (which skips
        // the shrink to keep its animation) or a 20 MB scanned PDF takes
        // its time on cellular too. The progress callback is what separates
        // "slow" from "stuck" for the person staring at the Uploading
        // button - a fixed clock here would kill a transfer that was
        // actually moving.
        await uploadBytesResumable(ref, body, { contentType }, onProgress);
      } else {
        // Small bodies only (shrunk photos land ~0.5-2 MB, most song files
        // and PDFs under a few MB): if this stalls past the timeout it is a
        // dead connection, not a big file.
        await withUploadTimeout(
          uploadBytes(ref, body, { contentType }),
          'That upload stalled. Check your connection and try again.'
        );
      }
      const url = await getDownloadURL(ref);
      return {
        id,
        kind: isVideo ? ('video' as const) : isFile ? ('file' as const) : ('image' as const),
        url,
        name: cleanAttachmentName(file.name || (isVideo ? 'Video' : isFile ? 'File' : 'Photo')),
        contentType,
        storagePath: uploadPath,
      };
    } catch (cause) {
      throw new Error(
        cause instanceof Error && cause.message
          ? cause.message
          : 'That upload failed. Check your connection and try again.'
      );
    }
  };
}

/**
 * The sender's own alias uid set, resolved WITHOUT a live directory listener.
 *
 * 2026-09-19 sweep finding: useSendMessage used to call useUsersList()
 * itself, which opens a full users-collection onSnapshot. ThreadRow calls
 * useSendMessage at its top level, so the Messages inbox was opening one
 * such listener PER CONVERSATION ROW - the exact "per-card listener" house
 * rule this repo forbids. This runs once, on an actual send, and only ever
 * touches the network at all for a caller who has already proven (locally,
 * for free) that she is a Jr Staff sign-in.
 *
 * NO QUERY FOR THE ORDINARY CALLER. Most senders have no studentId, and
 * jrStaffAliasUidsFor answers [self] for any account whose own doc does not
 * pass isOwnJrStaffSignIn (a parent sharing her dancer's studentId, for
 * instance) - both of those are decided from `realUserDoc` alone, so they
 * return before ever reaching Firestore.
 *
 * THE QUERY, WHEN IT RUNS, IS PROVABLE. shared/firestore.rules' users/{uid}
 * allow-read only lets an active staff caller list OTHER active-staff docs
 * (isActiveStaffByDoc() && resource.data.status == 'active' &&
 * isStaffRole(resource.data.role)) - see useUsersList's own comment on the
 * same trap. The filters below mirror that exactly (status 'active', role
 * in JR_STAFF_ALIAS_STAFF_ROLES, the same list isStaffRole checks), which is
 * also why this never finds an unpromoted dancer sign-in (status 'dancer'):
 * promotion is what rewrites her dancer account to active/'ensemble', and
 * that pair is the one shape isOwnJrStaffSignIn's 'active' branch covers.
 *
 * A DENIED OR FAILED LOOKUP DEGRADES TO TODAY'S EXACT PER-UID EXCLUSION,
 * never a blocked send - a missed self-exclusion is cosmetic (see the
 * caller's own comment), unlike useFindOrCreateDm's id mint.
 */
async function resolveSenderAliasUids(
  user: User,
  realUserDoc: UserDoc | null
): Promise<Set<string>> {
  const self = user.uid;
  let ownDoc = realUserDoc;
  // J32 re-pass finding 3 (P3): realUserDoc is null both when there is
  // genuinely no doc AND while useUserDoc's own listener has not delivered
  // its FIRST snapshot yet - typically right after sign-in, or a fast tap on
  // a freshly mounted composer, exactly the caller most likely to send in a
  // hurry. Treating null as "no studentId" silently dropped the widening for
  // her in that window. This reads her own doc once, directly, rather than
  // guessing "no link" from an absent snapshot.
  if (!ownDoc && db) {
    try {
      const ownSnap = await getDoc(doc(db, 'users', self));
      ownDoc = ownSnap.exists() ? (ownSnap.data() as UserDoc) : null;
    } catch (error) {
      console.warn('resolveSenderAliasUids: own-doc read failed', error);
      return new Set([self]);
    }
  }
  const studentId = ownDoc?.studentId ?? null;
  if (!studentId) return new Set([self]);
  const mine: JrStaffAliasAccount = {
    uid: self,
    role: ownDoc?.role ?? null,
    status: ownDoc?.status ?? null,
    studentId,
  };
  if (!isOwnJrStaffSignIn(mine)) return new Set([self]);
  if (!db) return new Set([self]);
  let accounts: JrStaffAliasAccount[] = [mine];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'users'),
        where('studentId', '==', studentId),
        where('status', '==', 'active'),
        where('role', 'in', JR_STAFF_ALIAS_STAFF_ROLES),
        // Matches the server's own ceiling (jrStaffAliasUids.mjs's
        // loadLinked) - one human's link query is bounded twice over.
        limit(JR_STAFF_ALIAS_MAX + 1)
      )
    );
    accounts = [mine, ...snap.docs.map((d) => d.data() as JrStaffAliasAccount)];
  } catch (error) {
    // A missed widening on one send is cosmetic (see useSendMessage's own
    // comment on this trade-off) - but silent was wrong. Report it so a
    // rules regression or an index gap is visible instead of invisible.
    console.warn('resolveSenderAliasUids: alias query failed', error);
    return new Set([self]);
  }
  return new Set(jrStaffAliasUidsFor({ uid: self, studentId, accounts }));
}

export function useSendMessage() {
  const { user } = useAuth();
  const { realUserDoc } = useUserDoc();
  return async (
    threadId: string,
    text: string,
    options: SendMessageOptions = {}
  ): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const trimmed = text.trim();
    if (plainMessageText(trimmed).length > 4000) throw new Error('Shorten the message to 4,000 visible characters.');
    const attachments = normalizeMessageAttachments(options.attachments);
    if (!trimmed && attachments.length === 0) throw new Error('Empty message.');

    const threadRef = doc(db, 'threads', threadId);
    const tSnap = await getDoc(threadRef);
    if (!tSnap.exists()) throw new Error('Conversation not found.');
    const thread = tSnap.data() as ThreadDoc;
    const participants = thread.participantUids ?? [];
    if (!participants.includes(user.uid)) {
      throw new Error('You are not in this conversation.');
    }
    const myAliasUids = await resolveSenderAliasUids(user, realUserDoc);
    const recipients = participants.filter((u) => !myAliasUids.has(u));
    const recipientSet = new Set(recipients);
    const mentioned = new Set(
      (options.mentions ?? []).filter((u) => recipientSet.has(u))
    );
    if (thread.familyThread === true) {
      const familyStudentId = thread.familyStudentIds?.[0] ?? '';
      const familyTitle = thread.title?.trim() ?? '';
      if (!familyStudentId || !familyTitle) {
        throw new Error('This family conversation needs studio repair before another message can be sent.');
      }
      if (mentioned.size > 0) {
        throw new Error('Whole-family follow-ups do not support @mentions.');
      }
      const result = await createServerFamilyMessage({
        studentId: familyStudentId,
        title: familyTitle,
        text: trimmed,
        ackRequired: options.ackRequired === true,
        ...(attachments.length > 0 ? { attachments } : {}),
        expectedThreadId: threadId,
      });
      return result.messageId;
    }

    const messageRef = doc(collection(db, 'threads', threadId, 'messages'));
    let sentThread = thread;
    let sentRecipients = recipients;
    let sentMentioned = mentioned;
    if (attachments.length > 0 || hasRichMessageFormatting(trimmed)) {
      const result = await saveMessageWithOptionalAttachments({
        user,
        action: 'threadMessage',
        targetId: threadId,
        writeId: messageRef.id,
        attachments,
        payload: {
          text: trimmed,
          mentions: [...mentioned],
          ackRequired: options.ackRequired === true,
        },
      });
      sentRecipients = result.recipientUids ?? recipients;
      sentMentioned = new Set(result.mentionedUids ?? [...mentioned]);
    } else await runTransaction(db, async (tx) => {
      const freshSnapshot = await tx.get(threadRef);
      if (!freshSnapshot.exists()) throw new Error('Conversation not found.');
      const freshThread = { id: freshSnapshot.id, ...freshSnapshot.data() } as ThreadDoc;
      if (freshThread.familyThread === true) {
        throw new Error('This family conversation changed. Refresh and try again.');
      }
      const freshParticipants = freshThread.participantUids ?? [];
      if (!freshParticipants.includes(user.uid)) {
        throw new Error('You are not in this conversation.');
      }
      const freshRecipients = freshParticipants.filter((uid) => !myAliasUids.has(uid));
      const freshRecipientSet = new Set(freshRecipients);
      const freshMentioned = new Set([...mentioned].filter((uid) => freshRecipientSet.has(uid)));
      const now = serverTimestamp();
      const inboundByUid: Record<string, Timestamp | FieldValue> = Object.fromEntries(
        freshRecipients.map((recipientUid) => [recipientUid, now])
      );

      tx.set(messageRef, {
        authorUid: user.uid,
        text: trimmed,
        mentions: [...freshMentioned],
        ackRequired: options.ackRequired === true,
        expectedAckUids: options.ackRequired === true ? freshRecipients : [],
        acks: {},
        readBy: { [user.uid]: now },
        ...(attachments.length > 0 ? { attachments } : {}),
        createdAt: now,
      });
      tx.update(threadRef, {
        // The rules compare this field to the STORED text verbatim under 200
        // characters, so it is threadInboxPreviewText, not the human preview.
        lastMessageText: threadInboxPreviewText(trimmed, attachments),
        lastMessageAt: now,
        lastMessageBy: user.uid,
        lastMessageId: messageRef.id,
        lastInboundAtByUid: inboundByUid,
      });
      sentThread = freshThread;
      sentRecipients = freshRecipients;
      sentMentioned = freshMentioned;
    });

    // Notify the other participants. @mentioned people get the stronger
    // 'mention' kind; everyone else gets a plain 'message'. Participants come
    // from the thread doc the sender can already read, so this works for any
    // sender (no users-list access needed). Best-effort.
    //
    // LAYER 2 of the round-4 Jr Staff board mirror fix (see
    // onMessageNotifyResult's own comment on SendMessageOptions): every
    // notifyUsers call actually made below is checked for real, reported
    // delivery - `serverReached` true and `inApp` greater than zero, not
    // merely "the request did not throw". A result with no server reach, a
    // non-2xx, a timeout, or a zero in-app count all read as NOT delivered.
    // A call this send never makes at all (no mentions, or
    // shouldNotifyThreadMessage says no) does not count against delivery -
    // there was nothing to deliver.
    let messageNotifyDelivered = true;
    const noteNotifyResult = (result: Awaited<ReturnType<typeof notifyUsers>>) => {
      if (!result || !result.serverReached || result.inApp <= 0) messageNotifyDelivered = false;
    };
    try {
      const others = sentRecipients.filter((u) => !sentMentioned.has(u));
      const senderName = messageNotificationSenderName(sentThread, realUserDoc ?? user);
      const communitySenderName = sentThread.communityOfferingId
        ? stampedParticipantLabel(sentThread.participantLabels, user.uid) ?? undefined
        : undefined;
      if (sentThread.communityOfferingId && !communitySenderName) {
        throw new Error('community notification sender label is missing');
      }
      const notifyOptions = communitySenderName
        ? communityMessageNotifyOptions(threadId, communitySenderName)
        : { thread: { id: threadId } };
      const preview = plainMessageText(messagePreviewText(trimmed, attachments)).slice(0, 120);
      const link = `/messages/${threadId}`;
      if (sentMentioned.size > 0) {
        noteNotifyResult(await notifyUsers([...sentMentioned], {
          kind: 'mention',
          title: `${senderName} mentioned you`,
          body: preview,
          link,
          // Codex round 5 on #801: the just-created message's own id, so a
          // durable-evidence reader (the Jr Staff board mirror's
          // verification) can bind to this EXACT send, never "some row for
          // this thread around this time" - see NotifyPayload.messageId's
          // own comment.
          messageId: messageRef.id,
        }, notifyOptions));
      }
      // DIRECT messages always notify; ordinary replies in a GROUP thread do
      // not (Bobbie, 2026-08-08: "Direct messages should always notify as
      // well. Replies to a group we don't need."). A one-to-one message is
      // addressed to you personally and there is nobody else to answer it, so
      // missing it is a real cost. A busy group thread is the opposite: every
      // "yes!" and "thank you!" would buzz twenty phones, which is how people
      // switch notifications off for good.
      //
      // A must-read is not an ordinary reply, so it stays in the fan-out for
      // groups too. That exemption has to be explicit: `ackRequired` is
      // otherwise only stored on the message and queried by the attention
      // pane, and nothing else in this file keys a notification off it. Gating
      // on `kind === 'dm'` alone silences the one category that most needs to
      // arrive.
      //
      // The other live exemption is @mentions, handled above. There is no
      // "urgent" here to defer to yet; it does not exist anywhere in the notify
      // path. See docs/NOTIFICATIONS-HANDOFF.md, where urgent-supersedes-digest
      // is specced but not built.
      //
      // The rule itself lives in threadNotifyDecision.ts so it can be tested.
      if (shouldNotifyThreadMessage({
        threadKind: sentThread.kind,
        adminBoard: sentThread.kind === 'group' && sentThread.adminBoard === true,
        mustRead: options.ackRequired === true,
        hasOtherRecipients: others.length > 0,
        classAnnouncement: options.classAnnouncement === true,
      })) {
        noteNotifyResult(await notifyUsers(others, {
          kind: 'message',
          title: `New message from ${senderName}`,
          body: preview,
          link,
          messageId: messageRef.id,
        }, notifyOptions));
      }
    } catch (e) {
      // Best-effort - the message already delivered - but never silent:
      // a swallowed throw here was one arm of the studio-wide "no
      // notifications for anything" silence (2026-08-07 diagnosis).
      console.error('sendThreadMessage: notify failed', e);
      messageNotifyDelivered = false;
    }
    if (sentThread.dancerStaffThread === true) {
      await requestDancerStaffThreadAlert(user, threadId, messageRef.id);
    }
    options.onMessageNotifyResult?.(messageNotifyDelivered);
    return messageRef.id;
  };
}

/**
 * Mark a message as acknowledged by the current user. Idempotent
 * (re-acking is a no-op write that just refreshes the timestamp).
 * Firestore rule allows this only when the only key changing inside
 * `acks` is the requester's own uid — no one can ack on someone
 * else's behalf, and no one can clear another person's ack.
 */
export function useAckMessage() {
  const { user } = useAuth();
  return async (
    threadId: string,
    messageId: string,
    familyThread = false,
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    if (familyThread) {
      await acknowledgeFamilyAttention(threadId, messageId);
      return;
    }
    await updateDoc(doc(db, 'threads', threadId, 'messages', messageId), {
      [`acks.${user.uid}`]: serverTimestamp(),
    });
  };
}

/**
 * Mark one or more delivered chat messages as read by the signed-in viewer.
 * Callers use this only for conversations with fewer than ten people. The
 * Firestore rule independently enforces that limit and permits only the
 * caller's own readBy slot.
 */
export function useMarkMessagesRead() {
  const { user } = useAuth();
  return async (threadId: string, messageIds: readonly string[]): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const ids = Array.from(new Set(messageIds.filter(Boolean)));
    for (let start = 0; start < ids.length; start += 100) {
      const batch = writeBatch(db);
      for (const messageId of ids.slice(start, start + 100)) {
        batch.update(doc(db, 'threads', threadId, 'messages', messageId), {
          [`readBy.${user.uid}`]: serverTimestamp(),
        });
      }
      await batch.commit();
    }
  };
}

export function useUploadMessageImageAttachment() {
  return useUploadMessageAttachmentAtPath(threadMessageAttachmentPath, MESSAGE_VIDEO_ATTACHMENT_MAX_BYTES);
}

/**
 * A whole-family thread is resolved by the server from the live guardian and
 * sibling graph, so its final thread id does not exist in the browser before
 * the first send. Stage that first message's files in the sender-owned folder;
 * familyMessage.mjs validates the exact caller prefix before persisting them.
 *
 * Uses PENDING_FAMILY_MESSAGE_VIDEO_MAX_BYTES, NOT the gateway's
 * MESSAGE_VIDEO_ATTACHMENT_MAX_BYTES above - this lane writes straight to
 * Storage and is bound by shared/storage.rules' isMessageVideoUpload(),
 * raised to the same 6 GB ceiling alongside this constant. See that
 * constant's own comment.
 */
export function useUploadPendingFamilyMessageAttachment() {
  const upload = useUploadMessageAttachmentAtPath(
    pendingFamilyMessageAttachmentPath,
    PENDING_FAMILY_MESSAGE_VIDEO_MAX_BYTES,
  );
  return (file: File, onProgress?: (fraction: number) => void) => (
    upload('pending-family-message', file, onProgress)
  );
}

/**
 * Set or clear the caller's reaction on one chat message. For an ordinary
 * thread, `alsoAck` stamps the same caller's acknowledgement in the same
 * write. Whole-family messages use the server acknowledgement lane first, so
 * their callers pass false here and keep the family attention record aligned.
 */
export function useReactToMessage() {
  const { user } = useAuth();
  return async (
    threadId: string,
    messageId: string,
    emoji: string | null,
    alsoAck: boolean
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    await updateDoc(doc(db, 'threads', threadId, 'messages', messageId), {
      [`reactions.${user.uid}`]: emoji ?? deleteField(),
      ...(alsoAck && emoji ? { [`acks.${user.uid}`]: serverTimestamp() } : {}),
    });
  };
}

/**
 * Recall a message: blank the text + clear mentions + set the recalled
 * tombstone. Only the original author can do this; the Firestore rule
 * enforces both ownership and that only those exact fields change. The
 * text must be emptied. Recalled messages render as "this message was
 * recalled" everywhere and stop surfacing as active mentions in Messages.
 *
 * Optional `originalText` lets us also scrub the thread's denormalized
 * inbox preview if the recalled message happened to be the most recent
 * one. The read, the decision and the write all happen inside one
 * transaction now (messageRecallWrite.ts, D6) so a reply landing in the
 * gap keeps its own preview instead of being overwritten with "Message
 * recalled".
 */
/**
 * Edit a message's text. The authenticated function rechecks the author and
 * deadline. Its transaction keeps an immutable before-edit revision and
 * refreshes the inbox preview when needed.
 */
export function useEditMessage() {
  const { user } = useAuth();
  return async (
    threadId: string,
    messageId: string,
    newText: string,
    expectedText: string,
    expectedRevisionCount: number,
    mentions?: string[]
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const trimmed = newText.trim();
    if (plainMessageText(trimmed).length > 4000) throw new Error('Shorten the message to 4,000 visible characters.');
    if (!plainMessageText(trimmed).trim()) throw new Error('Message cannot be empty.');
    let response: Response;
    try {
      response = await fetch('/.netlify/functions/messageEdit', {
        method: 'POST',
        headers: await functionHeaders(user),
        credentials: 'same-origin',
        body: JSON.stringify({
          threadId,
          messageId,
          text: trimmed,
          mentions: Array.from(new Set(mentions ?? [])),
          expectedText,
          expectedRevisionCount,
        }),
      });
    } catch {
      throw new Error('Could not reach the studio server. Nothing was changed.');
    }
    const result = (await response.json().catch(() => ({}))) as {
      ok?: boolean;
      reason?: string;
      error?: string;
    };
    if (!response.ok || !result.ok) {
      if (result.error === 'missing app check token' || result.error === 'invalid app check token') {
        throw new Error('This device could not be verified. Refresh and try your edit again. Nothing was changed.');
      }
      throw new Error(result.reason || `That edit did not save (${response.status}).`);
    }
  };
}

export function useRecallMessage() {
  return async (
    threadId: string,
    messageId: string,
    originalText?: string
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    await recallMessage(db, threadId, messageId, originalText);
  };
}
